import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Circle, Clock, AlertTriangle, Hourglass } from 'lucide-react';
import { clientApi, formatDate, STAGE_STATUS_LABELS, STAGE_STATUS_STYLES } from '../../components/client-portal/clientPortalApi';

const ICON = {
  completed: CheckCircle2,
  in_progress: Hourglass,
  waiting_on_client: AlertTriangle,
  waiting_on_lender: Clock,
  issue: AlertTriangle,
  not_started: Circle,
};

export default function ClientJourney() {
  const [stages, setStages] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await clientApi('/client/journey');
        if (cancel) return;
        setStages(d.stages || []);
        setProgress(d.progress_percent || 0);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="client-journey">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Your loan journey</h1>
        <p className="text-slate-500 mt-1 text-sm">Track every step from enquiry through to settlement and beyond.</p>
      </div>

      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Overall progress</span>
          <span className="text-sm font-semibold text-emerald-600">{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <ol className="relative border-l-2 border-slate-200 ml-4 sm:ml-6 space-y-4">
        {stages.map((s) => {
          const Icon = ICON[s.status] || Circle;
          const style = STAGE_STATUS_STYLES[s.status] || STAGE_STATUS_STYLES.not_started;
          const isDone = s.status === 'completed';
          return (
            <li key={s.stage_key} className="relative pl-6 sm:pl-8" data-testid={`journey-stage-${s.stage_key}`}>
              <span
                className={`absolute -left-[13px] sm:-left-[15px] top-1 w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-white ${
                  isDone ? 'bg-emerald-500 text-white' : s.status === 'not_started' ? 'bg-slate-100 text-slate-400' : 'bg-amber-500 text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="rounded-xl bg-white ring-1 ring-slate-200 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-900">{s.stage_label}</div>
                    {s.description && <div className="text-slate-500 text-sm mt-0.5">{s.description}</div>}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full ring-1 text-[11px] font-medium whitespace-nowrap ${style}`}>
                    {STAGE_STATUS_LABELS[s.status] || s.status}
                  </span>
                </div>
                {(s.notes || s.completed_at) && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                    {s.completed_at && <span>Completed {formatDate(s.completed_at)}</span>}
                    {s.notes && <span className="text-slate-700">{s.notes}</span>}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
